import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './VerificacaoAndaime.css';

/* ============================================================
   DOCUMENTOS — acesso mobile
   O que os colaboradores podem consultar no telemóvel: a avaliação de
   riscos, os documentos internos (com o estado da aceitação de quem está
   ligado), as instruções de trabalho e as medidas de autoproteção.
   O escritório escolhe o que aparece: nos documentos internos vão todos
   os que têm PDF; nas evidências de conformidade e na matriz do SGI, os
   marcados «visível aos colaboradores».
   ============================================================ */

// Pesquisa sem acentos nem maiúsculas (tira as marcas combinantes U+0300–U+036F)
const DIACRITICOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const normalizar = (s) => String(s || '').normalize('NFD').replace(DIACRITICOS, '').toLowerCase().trim();
const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '');
// No telemóvel o PDF abre no visualizador do sistema (zoom e deslizar); no PC, noutro separador
const ehTelemovel = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent || '')
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

const ROTULO_TIPO = { pdf: 'PDF', folha: 'Excel', texto: 'Word', imagem: 'Imagem', ficheiro: 'Ficheiro' };
// Extensão para o nome do ficheiro descarregado quando o Content-Disposition não
// se consegue ler (na rede local o pedido vai a outra porta e o browser esconde-o)
const EXTENSAO = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/msword': '.doc',
};

const DocumentosMobile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [dados, setDados] = useState(null);     // { trabalhador, grupos }
    const [erro, setErro] = useState('');
    const [aCarregar, setACarregar] = useState(false);
    const [aAbrir, setAAbrir] = useState(null);   // 'origem-id' a abrir
    const [pesquisa, setPesquisa] = useState('');

    const carregar = useCallback(async () => {
        setACarregar(true);
        try {
            const r = await api.get('/documentos-colaborador', { params: { _t: Date.now() } });
            setDados(r.data);
            setErro('');
        } catch {
            setErro('Não foi possível carregar os documentos. Verifique a ligação e tente outra vez.');
        } finally { setACarregar(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    // Ao voltar à app, atualizar: o escritório pode ter posto documentos à vista entretanto
    useEffect(() => {
        const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => document.removeEventListener('visibilitychange', aoVoltar);
    }, [carregar]);

    const abrir = async (doc) => {
        const chave = `${doc.origem}-${doc.id}`;
        if (aAbrir) return;
        setAAbrir(chave);
        try {
            const r = await api.get(`/documentos-colaborador/${doc.origem}/${doc.id}/ficheiro`, { responseType: 'blob', timeout: 90000 });
            const tipo = String(r.headers?.['content-type'] || r.data.type || 'application/octet-stream').split(';')[0];
            const url = URL.createObjectURL(new Blob([r.data], { type: tipo }));
            if (tipo === 'application/pdf' || tipo.startsWith('image/')) {
                if (ehTelemovel()) window.location.href = url;
                else { window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000); }
            } else {
                // Excel, Word…: o telemóvel oferece-se para o abrir na app certa
                const a = document.createElement('a');
                a.href = url;
                a.download = nomeDoFicheiro(r.headers?.['content-disposition']) || `${doc.titulo}${EXTENSAO[tipo] || ''}`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }
        } catch (e) {
            alert(e.response?.status === 404 ? 'Este documento já não está disponível.' : 'Não foi possível abrir o documento.');
        } finally { setAAbrir(null); }
    };

    const termo = normalizar(pesquisa);
    const grupos = (dados?.grupos || [])
        .map(g => ({ ...g, documentos: termo ? g.documentos.filter(d => normalizar(`${d.titulo} ${d.detalhe}`).includes(termo)) : g.documentos }))
        .filter(g => g.documentos.length > 0);
    const total = (dados?.grupos || []).reduce((n, g) => n + g.documentos.length, 0);
    const porAceitar = (dados?.grupos || []).flatMap(g => g.documentos).filter(d => d.aceitacao && !d.aceitacao.aceite).length;

    return (
        <div className="verificacao-andaimes-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>📚 Documentos</h1>
                        <p className="mobile-hide">Avaliação de riscos, documentos internos e instruções de segurança</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <button className="btn-secondary" style={btnTopo}
                        onClick={() => (user?.perfil_mobile ? navigate('/mobile-dashboard') : navigate(-1))}>
                        {user?.perfil_mobile ? '← Menu' : '← Voltar'}
                    </button>
                </div>
            </div>

            <div className="page-content" style={{ padding: 16 }}>
                <div className="va-content-wrapper">
                    <p style={intro}>
                        Documentos da empresa para consultar a qualquer momento. Toque em <b>Abrir</b> para ler.
                    </p>

                    {total > 6 && (
                        <input type="search" value={pesquisa} onChange={e => setPesquisa(e.target.value)}
                            placeholder="Procurar documento…" aria-label="Procurar documento" style={{ ...inp, marginBottom: 12 }} />
                    )}

                    {porAceitar > 0 && !termo && (
                        <div style={avisoAceitar}>
                            ✍️ {porAceitar === 1 ? 'Há 1 documento interno' : `Há ${porAceitar} documentos internos`} que ainda não
                            {porAceitar === 1 ? ' tem' : ' têm'} a sua aceitação registada. Leia-{porAceitar === 1 ? 'o' : 'os'} e fale com o escritório para assinar.
                        </div>
                    )}

                    {erro && <div className="va-message error">{erro}</div>}

                    {dados === null && !erro ? (
                        <div style={vazio}>A carregar…</div>
                    ) : dados && total === 0 ? (
                        <div style={vazio}>Ainda não há documentos disponíveis.<br />O escritório está a tratar disso.</div>
                    ) : grupos.length === 0 && termo ? (
                        <div style={vazio}>Nenhum documento encontrado.</div>
                    ) : grupos.map(g => (
                        <section key={g.chave} style={{ marginBottom: 18 }} aria-label={g.titulo}>
                            <h2 style={tituloGrupo}>
                                <span aria-hidden="true">{g.icone}</span> {g.titulo}
                                <span style={contagem}>{g.documentos.length}</span>
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {g.documentos.map(d => {
                                    const chave = `${d.origem}-${d.id}`;
                                    return (
                                        <div key={chave} style={cartao}>
                                            <div style={tituloDoc}>{d.titulo}</div>
                                            {d.detalhe && <div style={detalhe}>{d.detalhe}</div>}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                                {d.aceitacao && (d.aceitacao.aceite
                                                    ? <span style={badge('#dcfce7', '#166534')}>✅ Lido e aceite{d.aceitacao.em ? ` · ${fmtData(d.aceitacao.em)}` : ''}</span>
                                                    : <span style={badge('#fef3c7', '#92400e')}>✍️ Por assinar</span>)}
                                                {d.tipo !== 'pdf' && <span style={badge('#edf2f7', '#475569')}>{ROTULO_TIPO[d.tipo] || 'Ficheiro'}</span>}
                                                <button type="button" onClick={() => abrir(d)} disabled={!!aAbrir}
                                                    aria-label={`Abrir ${d.titulo}`} style={{ ...btnAbrir, opacity: aAbrir && aAbrir !== chave ? 0.6 : 1 }}>
                                                    {aAbrir === chave ? 'A abrir…' : '📄 Abrir'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}

                    {dados && total > 0 && (
                        <button type="button" onClick={carregar} disabled={aCarregar} style={btnAtualizar}>
                            {aCarregar ? 'A atualizar…' : '🔄 Atualizar'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// filename*=UTF-8''… (preferido) ou filename="…" do cabeçalho Content-Disposition
function nomeDoFicheiro(cd) {
    if (!cd) return null;
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    if (utf8) { try { return decodeURIComponent(utf8[1]); } catch { /* segue para o simples */ } }
    const simples = /filename="([^"]+)"/i.exec(cd);
    return simples ? simples[1] : null;
}

export default DocumentosMobile;

/* ---------- estilos ---------- */
const intro = { fontSize: 14, color: '#4a5568', lineHeight: 1.45, margin: '0 0 14px' };
const inp = {
    display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', minHeight: 44, fontSize: 16,
    padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: 8, background: '#fff', color: '#2d3748', fontFamily: 'inherit',
};
const avisoAceitar = { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '10px 12px', fontSize: 14, lineHeight: 1.45, marginBottom: 14 };
const tituloGrupo = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: '#1a202c', margin: '4px 0 10px' };
const contagem = { marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#64748b', background: '#edf2f7', borderRadius: 10, padding: '2px 8px' };
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const tituloDoc = { fontWeight: 700, fontSize: 15, color: '#1a202c', lineHeight: 1.3 };
const detalhe = { fontSize: 13, color: '#64748b', lineHeight: 1.4, marginTop: 3 };
const badge = (bg, fg) => ({ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const btnTopo = { minHeight: 44, whiteSpace: 'nowrap' };
const btnAbrir = {
    marginLeft: 'auto', minHeight: 44, minWidth: 110, padding: '8px 16px', borderRadius: 10, border: 'none',
    background: '#2b6cb0', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
};
const btnAtualizar = {
    display: 'block', width: '100%', minHeight: 44, marginTop: 4, borderRadius: 10, border: '1px solid #cbd5e0',
    background: '#fff', color: '#2d3748', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
};
const vazio = { color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 0', lineHeight: 1.5 };
